import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft } from "lucide-react";
import api from "../lib/api";
import { Attribution } from "../components/Attribution";
import { ChairCard } from "../components/ChairCard";
import { AdSlot } from "../components/AdSlot";

const Meta = ({ label, children }) => (
  <div className="py-4 border-b border-border">
    <p className="overline">{label}</p>
    <p className="text-base mt-1">{children}</p>
  </div>
);

export default function ChairDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null); setNotFound(false);
    api.get(`/chairs/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="max-w-3xl mx-auto px-5 py-32 text-center"><h1 className="serif text-4xl">Chair not found</h1><Link to="/catalog" className="text-accent underline mt-4 inline-block">Back to catalog</Link></div>;
  if (!data) return <div className="py-40 text-center text-muted-foreground">Loading…</div>;

  const { chair, related } = data;

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-6">
      <Link to="/catalog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-accent mb-6" data-testid="back-link">
        <ChevronLeft size={16} /> Catalog
      </Link>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        <div className="lg:sticky lg:top-20 self-start">
          <div className="border border-border bg-card">
            <img src={chair.image_url} alt={chair.title} className="w-full object-cover" data-testid="detail-image" />
          </div>
          <Attribution chair={chair} className="mt-3" />
        </div>

        <div>
          <p className="overline text-accent">{chair.movement}</p>
          <h1 className="serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-2 leading-[0.95]" data-testid="detail-title">{chair.title}</h1>
          <p className="text-lg text-muted-foreground mt-3">{chair.designer}, {chair.year}</p>

          <div className="mt-8">
            <p className="overline mb-3">History</p>
            <p className="text-base leading-relaxed" data-testid="detail-history">{chair.history}</p>
          </div>

          <div className="mt-8">
            <Meta label="Designer">
              <Link to={`/designers/${chair.designer_slug}`} className="hover:text-accent underline underline-offset-2" data-testid="detail-designer-link">{chair.designer}</Link>
            </Meta>
            <Meta label="Movement">
              <Link to={`/movements/${chair.movement_slug}`} className="hover:text-accent underline underline-offset-2" data-testid="detail-movement-link">{chair.movement}</Link>
            </Meta>
            <Meta label="Year / Era">{chair.year} · {chair.era}</Meta>
            <Meta label="Materials">{chair.materials.join(", ")}</Meta>
            <Meta label="Style">{chair.style}</Meta>
            <Meta label="Licensing">{chair.license || "See source"}</Meta>
          </div>

          <a href={chair.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 h-12 text-sm font-medium mt-8 hover:opacity-90 transition-opacity" data-testid="source-link-btn">
            View at {chair.source_name} <ArrowUpRight size={16} />
          </a>

          <div className="mt-10"><AdSlot height="h-28" /></div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-24">
          <p className="overline">You may also like</p>
          <h2 className="serif text-3xl mt-1 mb-8">Related chairs</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
            {related.map((c, i) => <ChairCard key={c.slug} chair={c} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
